<template>
	<div class="grid">
		<SpeV2Filters :currentUser="currentUser" :company="company" :vendorId="vendorId" @search="onFilterSearch"
			@clear="onFilterClear" />
		<div class="col-12 p-fluid mb-4 spe-v2-results">
			<div class="content-wrapper">
				<div class="content-header flex justify-content-between align-items-center flex-nowrap gap-2 mb-4">
					<span class="flex-1 text-left">
						Search Results
						<span v-if="selectedRows.length" class="selected-count ml-2">
							({{ selectedRows.length }} selected)
						</span>
					</span>
					<div class="flex-shrink-0">
						<Button v-if="isUserPurchasing()" label="Send Selected" icon="pi pi-send"
							class="p-button-success min-w-min px-3 py-2" style="min-width: unset; width: auto;"
							:disabled="!selectedRows.length || bulkSubmitting" @click="openBulkDialog" />
					</div>
				</div>
				<SpeV2ResultTable :value="dataList" v-model:selection="pageSelection" :loading="isLoading"
					:total-records="totalRecords" :show-selection="isUserPurchasing()" :show-supplier-name="true"
					:selection-mode="isUserPurchasing() ? 'multiple' : undefined" :status-label="statusLabel"
					:status-class="statusClass" @page="handlePagination" @row-select="onRowSelect"
					@row-unselect="onRowUnselect" @row-select-all="onRowSelectAll" @row-unselect-all="onRowUnselectAll"
					@view-detail="onViewDetail" @review-pdf="onReviewPdf" />

			</div>
		</div>

		<Dialog v-model:visible="showBulkDialog" header="Bulk Send to Supplier" :modal="true" appendTo="body"
			:style="bulkDialogStyle" :draggable="false" :resizable="false" :maximizable="false"
			class="spe-v2-bulk-dialog" :baseZIndex="200"
			@show="() => syncSpeV2DialogLayer('.spe-v2-bulk-dialog', 1300)">
			<p class="mb-3">
				You are about to send <b>{{ selectedRows.length }}</b> task(s) to supplier.
				Category for each task is taken from the selected task data.
			</p>

			<div v-if="bulkBlockedCount > 0" class="spe-v2-bulk-alert mb-3">
				<i class="pi pi-exclamation-triangle mr-2" />
				<span>
					{{ bulkBlockedCount }} supplier(s) have no mapping and are not registered in SPBI
					(highlighted in orange). Assign mapping from Actions, or send manually from task detail.
				</span>
			</div>

			<DataTable :value="selectedRows" class="p-datatable-sm mb-4 spe-v2-bulk-table" responsiveLayout="scroll"
				:scrollable="true" scrollHeight="flex" :rowClass="bulkRowClass">
				<Column header="Supplier" field="supplier_name">
					<template #body="{ data }">
						<div class="spe-v2-bulk-supplier">
							<span>{{ data.supplier_name || '-' }}</span>
							<small v-if="bulkMailingWarning(data)" class="spe-v2-bulk-warning-text">
								{{ bulkMailingWarning(data) }}
							</small>
						</div>
					</template>
				</Column>
				<Column header="Periode">
					<template #body="{ data }">S{{ data.semester }} {{ data.year }}</template>
				</Column>
				<Column header="Category" field="category">
					<template #body="{ data }">{{ data.category || '-' }}</template>
				</Column>
				<Column header="Method" field="method">
					<template #body="{ data }">{{ data.method || '-' }}</template>
				</Column>
				<Column header="Score" field="supplier_score">
					<template #body="{ data }">
						<Chip v-if="data.supplier_score != null && data.supplier_score !== ''" class="custom-chip"
							:class="scoreClassFor(data.supplier_score, data.total_result)">
							{{ scoreLabelFor(data.supplier_score) }}
						</Chip>
						<span v-else>-</span>
					</template>
				</Column>
				<Column header="Email Source">
					<template #body="{ data }">
						<Chip class="custom-chip" :class="bulkMailingChipClass(data)">
							{{ bulkMailingLabel(data) }}
						</Chip>
					</template>
				</Column>
				<Column header="Status">
					<template #body="{ data }">{{ statusLabel(data.status) }}</template>
				</Column>
				<Column header="Actions">
					<template #body="{ data }">
						<div class="flex align-items-center gap-1 flex-nowrap">
							<Button icon="pi pi-file-pdf" class="p-button-rounded p-button-text p-button-secondary"
								v-tooltip.top="'Review PDF'" @click.stop="onReviewPdf(data.scoring_id, data.category)" />
							<Button v-if="isBulkBlocked(data)" icon="pi pi-envelope" label="Assign"
								class="p-button-sm p-button-warning"
								v-tooltip.top="'Assign supplier email mapping'"
								@click.stop="openBulkMappingDialog(data)" />
						</div>
					</template>
				</Column>
			</DataTable>

			<div class="flex justify-content-end gap-2">
				<Button label="Cancel" class="p-button-outlined mr-2" @click="showBulkDialog = false" />
				<Button :label="bulkSubmitting ? 'Sending...' : 'Send to Supplier'" class="p-button-success"
					:disabled="bulkSubmitting || bulkChecking || bulkBlockedCount > 0" @click="handleBulkSubmit" />
			</div>
		</Dialog>

		<Dialog v-model:visible="showBulkMappingDialog" header="Assign Supplier Mapping" :modal="true"
			appendTo="body" :style="{ width: '40rem', maxWidth: '95vw' }" :draggable="false" :resizable="false"
			class="spe-v2-bulk-mapping-dialog" :baseZIndex="300"
			@show="() => syncSpeV2DialogLayer('.spe-v2-bulk-mapping-dialog', 1600)">
			<div class="p-fluid">
				<div class="field mb-3">
					<label class="block mb-2 font-medium">Supplier</label>
					<InputText :modelValue="bulkMappingForm.supplier_name" disabled />
				</div>
				<div class="field mb-3">
					<label class="block mb-2 font-medium">Company</label>
					<InputText :modelValue="bulkMappingForm.company_name" disabled />
				</div>
				<div class="field mb-3">
					<label class="block mb-2 font-medium">Supplier Type</label>
					<SelectButton v-model="bulkMappingForm.supplier_type" :options="supplierTypeOptions"
						optionLabel="label" optionValue="value" :allowEmpty="false" :disabled="bulkMappingSaving" />
					<small class="text-500">Local = Indonesia template · Foreign = English template</small>
				</div>
				<div class="field mb-3">
					<label class="block mb-2 font-medium">To</label>
					<Chips v-model="bulkMappingForm.email_to" separator="," :addOnBlur="true"
						placeholder="Enter one or more To emails" :disabled="bulkMappingSaving" />
				</div>
				<div class="field mb-3">
					<label class="block mb-2 font-medium">CC</label>
					<Chips v-model="bulkMappingForm.email_cc" separator="," :addOnBlur="true"
						placeholder="Enter one or more CC emails" :disabled="bulkMappingSaving" />
				</div>
			</div>
			<template #footer>
				<Button label="Cancel" icon="pi pi-times" class="p-button-outlined mr-2"
					:disabled="bulkMappingSaving" @click="showBulkMappingDialog = false" />
				<Button :label="bulkMappingSaving ? 'Saving...' : 'Save Mapping'" icon="pi pi-save"
					class="p-button-success" :disabled="bulkMappingSaving" @click="saveBulkMapping" />
			</template>
		</Dialog>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick, reactive, defineProps, defineEmits, defineExpose } from 'vue';
import { useToast } from 'primevue/usetoast';
import SpeV2Service from '../../service/SpeV2Service';
import SpeV2Filters from './SpeV2Filters.vue';
import SpeV2ResultTable from './SpeV2ResultTable.vue';
import { scoreClassFor, scoreLabelFor } from './speV2ScoreUtils';

const props = defineProps({
	currentUser: { type: Object, default: null },
	company: { type: Object, default: null },
	vendorId: { type: [String, Number], default: null },
});

const emit = defineEmits(['view-detail', 'review-pdf', 'refresh']);

const toast = useToast();
const dataList = ref([]);
const totalRecords = ref(0);
const isLoading = ref(false);
const lazyParams = ref({ first: 0, page: 0, rows: 10 });
const activeFilters = ref({});
const selectedRowsById = ref({});
const pageSelection = ref([]);
const showBulkDialog = ref(false);
const bulkSubmitting = ref(false);
const bulkChecking = ref(false);
const bulkMailingById = ref({});
const showBulkMappingDialog = ref(false);
const bulkMappingSaving = ref(false);
const bulkMappingScoringId = ref(null);
const bulkMappingForm = reactive({
	vendor_id: '',
	supplier_name: '',
	org_id: '',
	db_instance: '',
	company_name: '',
	supplier_type: 'LOCAL',
	email_to: [],
	email_cc: [],
});

const supplierTypeOptions = [
	{ label: 'Local (Indonesia)', value: 'LOCAL' },
	{ label: 'Foreign (English)', value: 'FOREIGN' },
];

const normalizeScoringId = (id) => String(id);

const selectedRows = computed(() => Object.values(selectedRowsById.value));

const bulkBlockedCount = computed(
	() => Object.values(bulkMailingById.value).filter((item) => item && !item.canSend).length
);

const getBulkMailingItem = (row) => bulkMailingById.value[normalizeScoringId(row?.scoring_id)] || null;

const isBulkBlocked = (row) => {
	const item = getBulkMailingItem(row);
	return Boolean(item && !item.canSend);
};

const bulkMailingWarning = (row) => getBulkMailingItem(row)?.warning || '';

const bulkMailingLabel = (row) => {
	const item = getBulkMailingItem(row);
	if (!item) return bulkChecking.value ? 'Checking...' : '-';
	if (item.mailingSource === 'portal') return 'Portal User';
	if (item.mailingSource === 'mapping') return 'SPE Mapping';
	return 'No Mapping';
};

const bulkMailingChipClass = (row) => {
	const item = getBulkMailingItem(row);
	if (!item) return 'chip-mailing-unknown';
	if (item.mailingSource === 'portal') return 'chip-mailing-portal';
	if (item.mailingSource === 'mapping') return 'chip-mailing-mapping';
	return 'chip-mailing-none';
};

const bulkRowClass = (row) => {
	const item = getBulkMailingItem(row);
	return item && !item.canSend ? 'spe-v2-bulk-row--blocked' : '';
};

const normalizeEmailItems = (items) => {
	const seen = new Set();
	return (Array.isArray(items) ? items : [items])
		.flatMap((item) => String(item || '').split(/[,\n;]+/))
		.map((item) => item.trim())
		.filter((item) => item)
		.filter((item) => {
			const key = item.toLowerCase();
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());

const refreshBulkMailingStatus = async () => {
	if (!selectedRows.value.length) {
		bulkMailingById.value = {};
		return;
	}

	bulkChecking.value = true;
	try {
		const res = await SpeV2Service.checkBulkMailingStatus({
			leveltenant: props.currentUser.leveltenant,
			scoringIds: selectedRows.value.map((row) => row.scoring_id),
		});
		const items = res?.data?.data?.items || [];
		const next = {};
		items.forEach((item) => {
			next[normalizeScoringId(item.scoringId)] = item;
		});
		bulkMailingById.value = next;
	} catch (err) {
		console.log(err);
		toast.add({
			severity: 'error',
			summary: err?.response?.data?.message || 'Failed to check supplier mailing status',
			life: 3000,
		});
	}
	bulkChecking.value = false;
};

const openBulkMappingDialog = (row) => {
	const mailing = getBulkMailingItem(row);
	bulkMappingScoringId.value = row.scoring_id;
	bulkMappingForm.vendor_id = String(row.vendor_id || mailing?.vendorId || '');
	bulkMappingForm.supplier_name = row.supplier_name || mailing?.vendorName || String(row.vendor_id || '');
	bulkMappingForm.org_id = String(row.org_id || props.company?.value || props.company?.org_id || '');
	bulkMappingForm.db_instance = row.db_instance || props.company?.db_instance || '';
	bulkMappingForm.company_name =
		mailing?.companyName || props.company?.name || props.company?.company_name || '';
	bulkMappingForm.supplier_type = mailing?.supplierType === 'FOREIGN' ? 'FOREIGN' : 'LOCAL';
	bulkMappingForm.email_to = [];
	bulkMappingForm.email_cc = [];
	showBulkMappingDialog.value = true;
};

const saveBulkMapping = async () => {
	bulkMappingForm.email_to = normalizeEmailItems(bulkMappingForm.email_to);
	bulkMappingForm.email_cc = normalizeEmailItems(bulkMappingForm.email_cc);

	if (
		!bulkMappingForm.vendor_id ||
		!bulkMappingForm.company_name ||
		!bulkMappingForm.org_id ||
		!bulkMappingForm.db_instance
	) {
		toast.add({ severity: 'warn', summary: 'Supplier and company are required', life: 3000 });
		return;
	}

	if (!bulkMappingForm.email_to.length || !bulkMappingForm.email_cc.length) {
		toast.add({ severity: 'warn', summary: 'To and CC emails are required', life: 3000 });
		return;
	}

	if (
		!bulkMappingForm.email_to.every(isValidEmail) ||
		!bulkMappingForm.email_cc.every(isValidEmail)
	) {
		toast.add({ severity: 'warn', summary: 'Invalid email format', life: 3000 });
		return;
	}

	bulkMappingSaving.value = true;
	try {
		const res = await SpeV2Service.saveEmailMapping({
			vendor_id: bulkMappingForm.vendor_id,
			supplier_name: bulkMappingForm.supplier_name,
			org_id: bulkMappingForm.org_id,
			db_instance: bulkMappingForm.db_instance,
			company_name: bulkMappingForm.company_name,
			supplier_type: bulkMappingForm.supplier_type || 'LOCAL',
			email_to: bulkMappingForm.email_to,
			email_cc: bulkMappingForm.email_cc,
		});

		if (res.status === 200) {
			toast.add({ severity: 'success', summary: 'Mapping saved', life: 2500 });
			showBulkMappingDialog.value = false;
			await refreshBulkMailingStatus();
		}
	} catch (err) {
		console.log(err);
		toast.add({
			severity: 'error',
			summary: err?.response?.data?.message || 'Failed to save mapping',
			life: 3000,
		});
	} finally {
		bulkMappingSaving.value = false;
	}
};

// Dialog bulk: tinggi menyesuaikan jumlah data, max 90vh
const bulkDialogStyle = computed(() => {
	const n = selectedRows.value.length;
	// ~56px per row + header + footer + padding ≈ 260px base
	const estimatedHeight = Math.min(260 + n * 56, window.innerHeight * 0.88);
	return {
		width: '95vw',
		maxWidth: '1300px',
		height: estimatedHeight + 'px',
	};
});

const clearSelectedRows = () => {
	selectedRowsById.value = {};
	pageSelection.value = [];
};

const syncPageSelection = () => {
	if (!Array.isArray(dataList.value)) {
		pageSelection.value = [];
		return;
	}

	pageSelection.value = dataList.value.filter((row) =>
		selectedRowsById.value[normalizeScoringId(row.scoring_id)]
	);
};

const onRowSelect = (event) => {
	if (!event?.data?.scoring_id) return;
	selectedRowsById.value = {
		...selectedRowsById.value,
		[normalizeScoringId(event.data.scoring_id)]: event.data,
	};
};

const onRowUnselect = (event) => {
	if (!event?.data?.scoring_id) return;
	const key = normalizeScoringId(event.data.scoring_id);
	const next = { ...selectedRowsById.value };
	delete next[key];
	selectedRowsById.value = next;
};

const onRowSelectAll = (event) => {
	const next = { ...selectedRowsById.value };
	(event.data || []).forEach((row) => {
		next[normalizeScoringId(row.scoring_id)] = row;
	});
	selectedRowsById.value = next;
};

const onRowUnselectAll = () => {
	if (!Array.isArray(dataList.value)) return;

	const next = { ...selectedRowsById.value };
	dataList.value.forEach((row) => {
		delete next[normalizeScoringId(row.scoring_id)];
	});
	selectedRowsById.value = next;
	syncPageSelection();
};

const isUserPurchasing = () => {
	const leveltenant = props.currentUser && props.currentUser.leveltenant;
	return leveltenant == 1 || leveltenant == 3;
};
const isUserSupplier = () => props.currentUser && props.currentUser.leveltenant == 2;

const statusLabel = (status) => {
	const map = { O: 'Outstanding', B: 'Sent', P: 'Completed', R: 'Rejected', D: 'Dismissed', PD: 'Pending Dismiss' };
	return map[status] || status;
};

const statusClass = (status) => {
	const map = {
		O: 'chip-outstanding',
		B: 'chip-submitted',
		P: 'chip-completed',
		R: 'chip-rejected',
		D: 'chip-dismissed',
		PD: 'chip-pending-dismiss',
	};
	return map[status] || '';
};

const canLoadData = () => {
	if (!props.company || !props.currentUser) return false;
	if (isUserSupplier() && !props.vendorId) return false;
	return true;
};

const buildFilterParams = () => {
	const params = {};
	const { year, semester, category, method, score_grade, vendor_id } = activeFilters.value;

	if (year != null && year !== '') params.year = year;
	if (semester != null && semester !== '') params.semester = semester;
	if (category) params.category = category;
	if (method) params.method = method;
	if (score_grade) params.score_grade = score_grade;
	if (vendor_id != null && vendor_id !== '') params.vendor_id = String(vendor_id);

	return params;
};

const loadData = async () => {
	if (!canLoadData()) return;

	isLoading.value = true;
	try {
		const params = {
			org_id: props.company.value,
			db_instance: props.company.db_instance,
			leveltenant: props.currentUser.leveltenant,
			page: lazyParams.value.page + 1,
			limit: lazyParams.value.rows,
			...buildFilterParams(),
		};

		if (isUserSupplier() && props.vendorId) {
			params.vendor_id = props.vendorId;
		}

		const res = await SpeV2Service.getOutstanding(params);
		if (res.status === 200) {
			dataList.value = Array.isArray(res.data.data) ? res.data.data : [];
			totalRecords.value = Number(res.data.count) || 0;
			syncPageSelection();
		}
	} catch (err) {
		console.log(err);
	}
	isLoading.value = false;
};

const handlePagination = (event) => {
	lazyParams.value = event;
	loadData();
};

const onFilterSearch = (filters) => {
	activeFilters.value = filters;
	lazyParams.value = { ...lazyParams.value, first: 0, page: 0 };
	loadData();
};

const onFilterClear = () => {
	activeFilters.value = {};
	lazyParams.value = { ...lazyParams.value, first: 0, page: 0 };
	clearSelectedRows();
	loadData();
};

const onViewDetail = (scoringId) => {
	emit('view-detail', scoringId);
};

const onReviewPdf = (scoringId, category) => {
	emit('review-pdf', scoringId, category);
};

const resolveTaskCategories = (row) => {
	if (row?.category) {
		const categories = String(row.category).split(',').map((item) => item.trim()).filter(Boolean);
		if (categories.length) return categories;
	}
	return ['RM'];
};

const syncSpeV2DialogLayer = (selector, zIndex) => {
	nextTick(() => {
		const dialog = document.querySelector(`.p-dialog${selector}`);
		if (!dialog) return;

		const mask = dialog.closest('.p-dialog-mask');
		dialog.style.setProperty('z-index', String(zIndex), 'important');
		if (mask) {
			mask.style.setProperty('z-index', String(zIndex - 1), 'important');
		}
	});
};

const openBulkDialog = async () => {
	if (!selectedRows.value.length) {
		toast.add({ severity: 'warn', summary: 'Select at least 1 task', life: 3000 });
		return;
	}

	const invalid = selectedRows.value.some((row) => row.status !== 'O' && row.status !== 'R');
	if (invalid) {
		toast.add({
			severity: 'warn',
			summary: 'Only Outstanding or Rejected tasks can be sent',
			life: 3000,
		});
		return;
	}

	bulkMailingById.value = {};
	showBulkDialog.value = true;
	await refreshBulkMailingStatus();
};

const handleBulkSubmit = async () => {
	if (bulkBlockedCount.value > 0) {
		toast.add({
			severity: 'warn',
			summary: 'Some suppliers still have no mapping / are not registered in SPBI',
			life: 3500,
		});
		return;
	}

	bulkSubmitting.value = true;
	try {
		const payload = {
			leveltenant: props.currentUser.leveltenant,
			username: props.currentUser.userid || props.currentUser.username || '',
			tasks: selectedRows.value.map((row) => ({
				scoringId: row.scoring_id,
				categories: resolveTaskCategories(row),
			})),
		};

		const res = await SpeV2Service.bulkSubmitEvaluation(payload);
		if (res.status === 200) {
			toast.add({ severity: 'success', summary: res.data.message || 'Successfully sent to supplier', life: 3000 });
			showBulkDialog.value = false;
			bulkMailingById.value = {};
			clearSelectedRows();
			emit('refresh');
			loadData();
		}
	} catch (err) {
		console.log(err);
		const message = err?.response?.data?.message || 'Failed to bulk submit';
		toast.add({ severity: 'error', summary: message, life: 4000 });
	}
	bulkSubmitting.value = false;
};

watch(
	() => [props.company, props.vendorId],
	() => {
		activeFilters.value = {};
		lazyParams.value = { first: 0, page: 0, rows: lazyParams.value.rows || 10 };
		clearSelectedRows();
		if (canLoadData()) {
			loadData();
		}
	}
);

onMounted(() => {
	if (canLoadData()) {
		loadData();
	}
});

defineExpose({
	reload: loadData,
	clearSelectedRows,
});
</script>

<style scoped>
.chip-outstanding {
	background: #3b82f6;
	color: #fff;
}

.chip-submitted {
	background: #0ea5e9;
	color: #fff;
}

.chip-completed {
	background: #10b981;
	color: #fff;
}

.chip-rejected {
	background: #ef4444;
	color: #fff;
}

.chip-dismissed {
	background: #6b7280;
	color: #fff;
}

.chip-pending-dismiss {
	background: #d97706;
	color: #fff;
}

.chip-score-excellent {
	background: #10b981;
	color: #fff;
}

.chip-score-good {
	background: #3b82f6;
	color: #fff;
}

.chip-score-average {
	background: #ff9800;
	color: #fff;
}

.chip-score-bad {
	background: #f59e0b;
	color: #fff;
}

.chip-score-poor {
	background: #ef4444;
	color: #fff;
}

.p-chip.custom-chip {
	padding: 4px 12px;
	border-radius: 8px;
	font-size: 12px;
	font-weight: 600;
}

.category-chip {
	display: inline-flex;
	align-items: center;
	padding: 8px 14px;
	border: 1px solid #d1d5db;
	border-radius: 8px;
	cursor: pointer;
	user-select: none;
}

.category-chip--selected {
	border-color: #10b981;
	background: #ecfdf5;
	color: #047857;
}

.selected-count {
	font-size: 0.875rem;
	color: #047857;
	font-weight: 600;
}

.spe-v2-bulk-alert {
	display: flex;
	align-items: flex-start;
	gap: 0.25rem;
	padding: 0.75rem 1rem;
	border-radius: 8px;
	background: #fff7ed;
	border: 1px solid #fdba74;
	color: #9a3412;
	font-size: 0.9rem;
	line-height: 1.4;
}

.spe-v2-bulk-supplier {
	display: flex;
	flex-direction: column;
	gap: 0.2rem;
}

.spe-v2-bulk-warning-text {
	color: #c2410c;
	font-weight: 600;
}

.chip-mailing-portal {
	background: #10b981;
	color: #fff;
}

.chip-mailing-mapping {
	background: #3b82f6;
	color: #fff;
}

.chip-mailing-none {
	background: #ea580c;
	color: #fff;
}

.chip-mailing-unknown {
	background: #94a3b8;
	color: #fff;
}
</style>

<style>
/* Bulk dialog — full height, content scrollable */
.spe-v2-bulk-dialog.p-dialog {
	display: flex;
	flex-direction: column;
}

.spe-v2-bulk-dialog .p-dialog-content {
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
}

.spe-v2-bulk-dialog .p-dialog-header {
	cursor: default !important;
}

.spe-v2-bulk-mapping-dialog.p-dialog {
	z-index: 1600;
}

.spe-v2-bulk-table .p-datatable-thead > tr > th {
	background: #ffecec !important;
	color: #555555 !important;
}

.spe-v2-bulk-table .spe-v2-bulk-row--blocked > td {
	background: #ffedd5 !important;
}

.spe-v2-bulk-table .spe-v2-bulk-row--blocked:hover > td {
	background: #fed7aa !important;
}
</style>

